import { useQuery } from '@apollo/client';

import type { Locale } from 'bundles/shared-articles/types';
import type {
  SeoContentHubPageExistsQueryQuery,
  SeoContentHubPageExistsQueryQueryVariables,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { DEFAULT_COUNTRY_CODE, DEFAULT_LOCALE_CODE, PAGE_TYPE } from 'bundles/unified-seo-content-common/constants';
import { seoContentHubPageExistsQuery } from 'bundles/unified-seo-content-common/queries/SeoContentHubPageQuery';
import type { SEOContentPageType } from 'bundles/unified-seo-content-common/types';
import { getPageLink, queryErrorHandler } from 'bundles/unified-seo-content-common/utils';

type InnerProps = {
  pageType: SEOContentPageType;
  preview?: boolean;
  subfolderLocaleCode?: Locale;
  skipQuery?: boolean;
};

const useSeoContentHubPageLink = ({
  pageType,
  preview = false,
  subfolderLocaleCode = DEFAULT_LOCALE_CODE,
  skipQuery = false,
}: InnerProps): string => {
  const { languageCode, countryCode } = subfolderLocaleCode;

  const { data, loading } = useQuery<SeoContentHubPageExistsQueryQuery, SeoContentHubPageExistsQueryQueryVariables>(
    seoContentHubPageExistsQuery,
    {
      variables: {
        pageType,
        preview,
        countryCode,
        languageCode,
      },
      skip: skipQuery || countryCode === DEFAULT_COUNTRY_CODE,
      context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
      errorPolicy: 'all',
      onError: queryErrorHandler({ extraMessage: 'Error fetching SEO contenthub page data' }),
    }
  );

  const hasLocalizedHubPage = !!data?.hubPageWithCatgoriesData?.total;

  const pageLink =
    !loading && hasLocalizedHubPage
      ? getPageLink({
          localizedCountryCode: countryCode,
          pageType: PAGE_TYPE.hubWithCategories,
          seoContentPageType: pageType,
        })
      : getPageLink({ pageType: PAGE_TYPE.hubWithCategories, seoContentPageType: pageType });
  return pageLink;
};

export default useSeoContentHubPageLink;
